import { type ReactNode, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ThreadListPrimitive, useAuiState } from '@assistant-ui/react';
import { ArrowLeft, Plus } from 'lucide-react';
import { SimpleProjectSelector } from 'src/components/SimpleProjectSelector';
import { UserMenu } from 'src/components/UserMenu';
import { SidebarLayout } from 'src/layout/SidebarLayout/SidebarLayout';

import { Skeleton } from 'components/Skeleton';

import { andiChatPath } from '../../constants';
import { useOnAndiPage } from '../../hooks/useOnAndiPage';

import { ChatListItem } from './ChatListItem';

import classes from './andiChatsSidebar.module.css';

const LOADING_ROW_WIDTHS = ['72%', '58%', '80%', '52%', '66%'];
const ITEM_COMPONENTS = { ThreadListItem: ChatListItem };

/** The rows, once the list has loaded the first time. Before that the open chat may already have a row of its
 *  own, and showing it alone flashes one chat, then all of them. Reloads keep the rows up. */
const ChatRows = () => {
  const loading = useAuiState((state) => state.threads.isLoading);
  const count = useAuiState((state) => state.threads.threadIds.length);
  const failed = useAuiState((state) => Boolean(state.threads.loadError));
  const [loadedOnce, setLoadedOnce] = useState(false);
  if (!loading && !loadedOnce) setLoadedOnce(true);

  if (!loadedOnce && loading) {
    return LOADING_ROW_WIDTHS.map((width) => (
      <div key={width} className={classes.loadingRow}><Skeleton width={width} height="13px" /></div>
    ));
  }
  if (failed && count === 0) {
    return <div className={classes.note}>Couldn&apos;t load your chats. Refresh to try again.</div>;
  }
  if (count === 0) return <div className={classes.note}>No chats yet.</div>;
  return <ThreadListPrimitive.Items components={ITEM_COMPONENTS} />;
};

type AndiChatsSidebarProps = {
  /** The sidebar anywhere else. */
  children: ReactNode;
};

/** On an Andi page, the app's sidebar lists Andi's chats instead of the project's navigation, so the page
 *  itself is all chat and plan; Back returns to the navigation. Anywhere else, it is `children`. */
export const AndiChatsSidebar = ({ children }: AndiChatsSidebarProps) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const onAndiPage = useOnAndiPage();
  if (!onAndiPage) return children;

  return (
    <SidebarLayout.Sidebar>
      <SidebarLayout.SidebarHeader>
        <SimpleProjectSelector />
      </SidebarLayout.SidebarHeader>
      <SidebarLayout.SidebarMain>
        <div className={classes.sidebar}>
          <button type="button" className={classes.nav} onClick={() => navigate(`/projects/${projectId}`)}>
            <ArrowLeft size={20} />
            Back
          </button>
          <ThreadListPrimitive.Root className={`${classes.list} ${classes.chats}`}>
            {/* The URL decides the open chat, so a new chat is the chat-less path. assistant-ui's New button
                switches threads itself, and fights it. */}
            <button type="button" className={classes.nav} onClick={() => navigate(andiChatPath(projectId))}>
              <Plus size={20} />
              New chat
            </button>
            <div className={classes.rows}>
              <ChatRows />
            </div>
          </ThreadListPrimitive.Root>
        </div>
        <UserMenu />
      </SidebarLayout.SidebarMain>
    </SidebarLayout.Sidebar>
  );
};
